import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = {
  take_home: "income", savings_goal_amount: "goal",
  housing_pct: "h", transport_pct: "tr", groceries_pct: "gr", utilities_pct: "ut",
  insurance_pct: "in", savings_pct: "sv", debt_pct: "dt", entertainment_pct: "en",
  clothing_pct: "cl", children_pct: "ch", other_pct: "ot"
}
const URL_TYPES = {
  take_home: "number", savings_goal_amount: "number",
  housing_pct: "number", transport_pct: "number", groceries_pct: "number",
  utilities_pct: "number", insurance_pct: "number", savings_pct: "number",
  debt_pct: "number", entertainment_pct: "number", clothing_pct: "number",
  children_pct: "number", other_pct: "number"
}

const CATEGORY_KEYS = [
  "housing", "transport", "groceries", "utilities", "insurance",
  "savings", "debt", "entertainment", "clothing", "children", "other"
]

const BAR_COLORS = [
  "#0e7490", "#0891b2", "#06b6d4", "#22d3ee", "#67e8f9",
  "#059669", "#10b981", "#f59e0b", "#f97316", "#ef4444", "#8b5cf6"
]

export default class extends Controller {
  static targets = [
    "takeHomeInput",
    "housingPctInput", "transportPctInput", "groceriesPctInput",
    "utilitiesPctInput", "insurancePctInput", "savingsPctInput",
    "debtPctInput", "entertainmentPctInput", "clothingPctInput",
    "childrenPctInput", "otherPctInput",
    "housingAmountDisplay", "transportAmountDisplay", "groceriesAmountDisplay",
    "utilitiesAmountDisplay", "insuranceAmountDisplay", "savingsAmountDisplay",
    "debtAmountDisplay", "entertainmentAmountDisplay", "clothingAmountDisplay",
    "childrenAmountDisplay", "otherAmountDisplay",
    "totalPctDisplay",
    "savingsGoalInput",
    "resultsPanel", "loadingIndicator", "introContent",
    "heroAllocated", "heroRemaining", "heroSubtext",
    "stackedBar", "stackedLegend",
    "categoriesBody",
    "savingsGoalCard", "sgTarget", "sgAnnual", "sgMonthly", "sgFortnightly", "sgMonths",
    "frequencyBody",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 3 })
    this._readUrlParams()
    this._updateAmountDisplays()
    this._renderPrerenderedExtras()
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._updateAmountDisplays()
    this._debounce.schedule(detectEventType(event), this.takeHomeInputTarget.value)
  }

  pctChanged() {
    this._updateAmountDisplays()
    this._debounce.schedule("toggle", this.takeHomeInputTarget.value)
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _readUrlParams() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (p.take_home && this.hasTakeHomeInputTarget) {
      this.takeHomeInputTarget.value = p.take_home.toLocaleString()
    }
    if (p.savings_goal_amount && this.hasSavingsGoalInputTarget) {
      this.savingsGoalInputTarget.value = p.savings_goal_amount.toLocaleString()
    }
    CATEGORY_KEYS.forEach(key => {
      const val = p[`${key}_pct`]
      if (val != null) {
        const target = `${key}PctInputTarget`
        if (this[target]) this[target].value = val
      }
    })
  }

  _updateAmountDisplays() {
    const takeHome = parseNumber(this.takeHomeInputTarget.value) || 0
    let totalPct = 0

    CATEGORY_KEYS.forEach(key => {
      const pctTarget = `${key}PctInput`
      const amountTarget = `${key}AmountDisplay`
      if (this[`has${this._capitalize(pctTarget)}Target`]) {
        const pct = parseFloat(this[`${pctTarget}Target`].value) || 0
        totalPct += pct
        if (this[`has${this._capitalize(amountTarget)}Target`]) {
          const amount = takeHome > 0 ? (takeHome * pct / 100) : 0
          this[`${amountTarget}Target`].textContent = takeHome > 0 ? formatCurrency(amount) : "-"
        }
      }
    })

    if (this.hasTotalPctDisplayTarget) {
      this.totalPctDisplayTarget.textContent = `${totalPct.toFixed(0)}%`
      this.totalPctDisplayTarget.classList.toggle("text-red-600", totalPct > 100)
      this.totalPctDisplayTarget.classList.toggle("text-green-600", totalPct <= 100)
    }
  }

  _capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  async _performCalculation() {
    const takeHome = parseNumber(this.takeHomeInputTarget.value)
    if (!takeHome || takeHome <= 0) return

    this._showLoading()

    try {
      const params = {
        type: "budget_planner",
        take_home: takeHome,
        savings_goal_amount: parseNumber(this.savingsGoalInputTarget.value) || 0
      }

      CATEGORY_KEYS.forEach(key => {
        const target = `${key}PctInputTarget`
        if (this[target]) {
          params[`${key}_pct`] = parseFloat(this[target].value) || 0
        }
      })

      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("budget_planner", { salaryAmount: takeHome })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    // Hero
    this.heroAllocatedTarget.textContent = formatCurrency(data.total_allocated)
    const remaining = data.remaining
    this.heroRemainingTarget.textContent = formatCurrency(remaining)
    this.heroRemainingTarget.classList.toggle("text-green-600", remaining >= 0)
    this.heroRemainingTarget.classList.toggle("text-red-600", remaining < 0)
    this.heroSubtextTarget.textContent =
      `${data.total_allocated_pct}% of ${formatCurrency(data.take_home)} allocated`

    // Stacked bar
    if (data.categories && this.hasStackedBarTarget) {
      this.stackedBarTarget.innerHTML = data.categories.map((c, i) => {
        const width = Math.max(c.percentage, 0)
        if (width <= 0) return ""
        return `<div style="width:${width}%;background:${BAR_COLORS[i % BAR_COLORS.length]}" title="${c.label}: ${c.percentage}%"></div>`
      }).join("")

      this.stackedLegendTarget.innerHTML = data.categories.map((c, i) => {
        return `<span class="flex items-center gap-1 text-xs text-gray-600">
          <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:${BAR_COLORS[i % BAR_COLORS.length]}"></span>
          ${c.label}
        </span>`
      }).join("")
    }

    // Category table
    if (data.categories && this.hasCategoriesBodyTarget) {
      this.categoriesBodyTarget.innerHTML = data.categories.map(c => {
        const diff = c.vs_benchmark
        const diffText = diff > 0 ? `+${diff.toFixed(1)}%` : diff < 0 ? `${diff.toFixed(1)}%` : "At benchmark"
        const diffClass = diff > 0 ? "text-amber-600" : diff < 0 ? "text-green-600" : "text-gray-500"
        return `<tr>
          <td class="px-4 py-2.5 text-gray-700">${c.label}</td>
          <td class="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">${c.percentage}%</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-gray-500">${c.benchmark_pct}%</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-cyan-700">${formatCurrency(c.amount)}</td>
          <td class="px-4 py-2.5 text-right tabular-nums ${diffClass}">${diffText}</td>
        </tr>`
      }).join("")
    }

    // Savings goal
    if (data.savings_goal && this.hasSavingsGoalCardTarget) {
      this.savingsGoalCardTarget.classList.remove("hidden")
      this.sgTargetTarget.textContent = formatCurrency(data.savings_goal.target_amount)
      this.sgAnnualTarget.textContent = formatCurrency(data.savings_goal.annual_savings)
      this.sgMonthlyTarget.textContent = formatCurrency(data.savings_goal.monthly_savings)
      this.sgFortnightlyTarget.textContent = formatCurrency(data.savings_goal.fortnightly_savings)
      const months = data.savings_goal.months_to_goal
      const years = Math.floor(months / 12)
      const rem = months % 12
      const timeText = years > 0 ? `${years}y ${rem}m` : `${months} months`
      this.sgMonthsTarget.textContent = timeText
    } else if (this.hasSavingsGoalCardTarget) {
      this.savingsGoalCardTarget.classList.add("hidden")
    }

    // Frequency table
    if (data.frequencies && this.hasFrequencyBodyTarget) {
      const periods = [
        ["Annual", data.frequencies.annual],
        ["Monthly", data.frequencies.monthly],
        ["Fortnightly", data.frequencies.fortnightly],
        ["Weekly", data.frequencies.weekly]
      ]
      this.frequencyBodyTarget.innerHTML = periods.map(([label, f]) => {
        return `<tr>
          <td class="px-4 py-2.5 text-gray-700">${label}</td>
          <td class="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">${formatCurrency(f.take_home)}</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-cyan-700">${formatCurrency(f.remaining)}</td>
        </tr>`
      }).join("")
    }

    this._showResults()

    // ARIA
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent =
        `Budget calculated. ${formatCurrency(data.total_allocated)} allocated, ${formatCurrency(remaining)} remaining.`
    }
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      this._renderResults(data)
    } catch { /* ignore parse errors */ }
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
